import type { ActivityEvent, Message, ReviewDecision, WorkItem, WorkItemDetail } from '@deskworks/shared-types';
import { ACTIVE_EXECUTION_STATUSES, TERMINAL_WORK_ITEM_STATUSES } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Bot, CircleCheck, CircleX, Crown, GitBranch, Info, MessageCircleQuestion, MessageSquare, Moon, Plus, Send, ShieldCheck, User, Zap } from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { Markdown } from '@/components/markdown';
import { Section } from '@/components/section';
import { ErrorState, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { formatAbsolute, formatTime } from '@/lib/format';
import { REVIEW_DECISION } from '@/lib/labels';
import { inSentence, t } from '@/lib/i18n';
import { useApiMutation, useIsPhone } from '@/lib/hooks';
import { useChatInput } from '@/lib/use-chat-input';
import { ChatScreen } from '@/components/chat/chat-screen';
import { qk } from '@/lib/query-keys';
import { useAutoScroll } from '@/lib/use-auto-scroll';
import { cn } from '@/lib/utils';
import { SessionLink } from '@/components/session-link';
import { AgentSteps, currentTurn } from '@/components/agent-steps';
import { readMarks, ReadMark, type Mark } from '@/components/chat/read-mark';
import { AgentAvatar } from '@/components/agent-avatar';
import { VickiActionCard } from '@/components/front-desk/vicki-action-card';
import { WorkItemStatusBadge } from '@/components/status-badge';
import { childrenDone, WorkLog } from './work-log';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

export function isAgentRunning(item: WorkItemDetail): boolean {
  return !!item.currentExecution && ACTIVE_EXECUTION_STATUSES.includes(item.currentExecution.status);
}

/**
 * A person can talk to the desk's Lead beside the work (docs/features/work-item-chat.md#talking-to-the-lead) — not
 * where the thread is a chat with the Lead anyway: a finished item's follow-up, an open question.
 */
function canTalkToLead(item: WorkItemDetail): boolean {
  if (!item.lead || item.kind === 'review' || TERMINAL_WORK_ITEM_STATUSES.includes(item.status)) return false;
  return !(item.questionOpen && item.status === 'waiting' && !item.paused);
}

export function Chat({
  item,
  messages,
  activity,
  isPending,
  error,
  onRetry,
  end,
}: {
  item: WorkItemDetail;
  messages: Message[] | undefined;
  /** What happened besides the messages: the steps of each run, who created the item. */
  activity?: ActivityEvent[];
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
  /** What closes the course: the result, the card to decide on. */
  end?: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [leadChosen, setLeadChosen] = useState(false);
  // Every run of the Lead and its agents, and what each wrote down it did (docs/features/work-items.md#work-history).
  const runs = useQuery({ queryKey: qk.workItemExecutions(item.id), queryFn: () => api.workItems.executions(item.id) });
  const history = useQuery({ queryKey: qk.workItemHistory(item.id), queryFn: () => api.workItems.history(item.id) });
  const createdBy = (activity ?? []).find((e) => e.type === 'work_item.created')?.agentName ?? null;
  const running = isAgentRunning(item);
  const ref = useAutoScroll<HTMLDivElement>(
    `${messages?.length ?? 0}:${runs.data?.length ?? 0}:${history.data?.length ?? 0}:${item.status}:${running}:${item.approvals.length}`,
  );
  const terminal = TERMINAL_WORK_ITEM_STATUSES.includes(item.status);
  const leadPossible = canTalkToLead(item);
  const toLead = leadPossible && leadChosen;
  const lead = item.lead?.name ?? t('the Lead');
  const sideChat = !!item.sideChat && ACTIVE_EXECUTION_STATUSES.includes(item.sideChat.status);

  const send = useApiMutation((content: string) => api.workItems.sendMessage(item.id, toLead ? { content, toLead } : { content }), {
    errorTitle: t('Couldn’t send message'),
    onSuccess: (msg) => {
      setDraft('');
      queryClient.setQueryData<Message[]>(qk.workItemMessages(item.id), (old) =>
        old && !old.some((m) => m.id === msg.id) ? [...old, msg] : old,
      );
    },
    invalidate: [qk.workItem(item.id)],
  });

  const submit = () => {
    const content = draft.trim();
    if (content && !send.isPending) send.mutate(content);
  };
  const input = useChatInput(draft, submit);
  const phone = useIsPhone();
  // On a phone the course opens as a chat of its own (docs/features/mobile.md#chats); the page shows its last messages.
  const [open, setOpen] = useState(false);

  const followUp = running && item.currentExecution?.kind === 'follow_up';
  // The run stays a while for more questions; it is only working while the person spoke last.
  const turn = followUp && item.currentExecution ? currentTurn(messages ?? [], item.currentExecution.createdAt) : null;
  // A run on the work (not a follow-up): what the agent at work read of what the person wrote to it since it started.
  const working = running && !followUp ? item.currentExecution : null;
  const marks = working || turn?.open ? readMarks(messages ?? [], item.currentExecution, working ? 'run' : 'reply') : null;
  const agentName = item.currentExecution?.agentName ?? item.assignedAgentName ?? t('The agent');
  let hint: { icon: ReactNode; text: string; tone: string };
  if (toLead && sideChat) {
    hint = {
      icon: <Zap className="size-3.5" />,
      text: t('{lead} is here and answering beside the work — the run in progress goes on.', { lead }),
      tone: 'text-indigo-700 dark:text-indigo-300',
    };
  } else if (toLead) {
    hint = {
      icon: <Crown className="size-3.5" />,
      text: item.sideChatRequested
        ? t('{lead} is being woken up to answer you …', { lead })
        : t('{lead} answers you here beside the work — the run in progress goes on. What you decide, {lead} passes on to the work.', { lead }),
      tone: 'text-indigo-700 dark:text-indigo-300',
    };
  } else if (followUp) {
    hint = {
      icon: <Zap className="size-3.5" />,
      text: t('The agent is here and answering — it stays a few minutes for follow-up questions.'),
      tone: 'text-indigo-700 dark:text-indigo-300',
    };
  } else if ((terminal || item.questionOpen) && item.followUpRequested) {
    hint = {
      icon: <Zap className="size-3.5" />,
      text: t('The agent is being woken up to answer you …'),
      tone: 'text-indigo-700 dark:text-indigo-300',
    };
  } else if (running) {
    hint = {
      icon: <Zap className="size-3.5" />,
      text: t('The agent is working right now and will see your message immediately.'),
      tone: 'text-indigo-700 dark:text-indigo-300',
    };
  } else if (item.questionOpen && !item.paused) {
    hint = {
      icon: <MessageCircleQuestion className="size-3.5" />,
      text: t('A question is open — your message is answered here, and the work goes on once it is answered.'),
      tone: 'text-amber-700 dark:text-amber-300',
    };
  } else if (terminal) {
    hint = {
      icon: <Info className="size-3.5" />,
      text: t('Finished — ask the agent about the result, or ask for changes and it reopens the Work Item.'),
      tone: 'text-muted-foreground',
    };
  } else if (item.paused) {
    hint = {
      icon: <Moon className="size-3.5" />,
      text: t('This Work Item is paused. The agent will read your message once you resume it.'),
      tone: 'text-muted-foreground',
    };
  } else {
    hint = {
      icon: <Moon className="size-3.5" />,
      text: t('The agent is asleep — your message will wake it up.'),
      tone: 'text-muted-foreground',
    };
  }

  const message = (m: Message) => <ChatMessage message={m} workspaceId={item.workspaceId} mark={marks?.get(m.id)} agentName={agentName} />;
  const thread =
    isPending || runs.isPending ? (
      <LoadingRows rows={3} />
    ) : error ? (
      <ErrorState error={error} onRetry={onRetry} />
    ) : (
      <WorkLog
        item={item}
        messages={messages ?? []}
        activity={activity ?? []}
        runs={runs.data ?? []}
        history={history.data ?? []}
        createdBy={createdBy}
        live={turn && <AgentSteps executionId={item.currentExecution?.id} turn={turn} working={turn.open} agentName={agentName} />}
        end={end}
        renderMessage={message}
        renderChild={(child, back) => <SubItemEntry child={child} back={back} parent={item} />}
      />
    );

  const composer = (
    <div className="border-t bg-muted/30 px-3 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] sm:px-5 sm:py-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className={cn('flex min-w-0 items-center gap-1.5 text-xs', hint.tone)}>
          <span className="shrink-0">{hint.icon}</span>
          <span className="min-w-0">{hint.text}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {leadPossible && (
            <label
              className={cn(
                'inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
                toLead ? 'border-indigo-300 bg-indigo-50 text-indigo-800 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200' : 'text-muted-foreground',
              )}
              title={t('Ask the Lead about the work, or change its course, without interrupting the agent at work.')}
            >
              <Crown className="size-3.5" />
              {t('Talk to {lead} (Lead)', { lead })}
              <Switch checked={toLead} onCheckedChange={setLeadChosen} className="ml-0.5" />
            </label>
          )}
          {item.kind !== 'watch' && item.kind !== 'report' && <AddWhatYouDid item={item} />}
        </div>
      </div>
      <div className="flex items-end gap-2">
        <Textarea
          ref={input.ref}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={input.onKeyDown}
          placeholder={input.placeholder(
            toLead
              ? t('Message {lead} … (Enter to send, Shift+Enter for a new line)', { lead })
              : t('Message the agent … (Enter to send, Shift+Enter for a new line)'),
          )}
          rows={1}
          enterKeyHint={input.touch ? 'enter' : 'send'}
          autoFocus={phone && open}
          className="max-h-40 min-h-11 resize-none bg-card py-2.5 sm:max-h-48"
          aria-label={t('Message')}
        />
        <Button onClick={submit} loading={send.isPending} disabled={!draft.trim()} aria-label={t('Send message')} className="h-11">
          {!send.isPending && <Send />}
          <span className="hidden sm:inline">{t('Send')}</span>
        </Button>
      </div>
    </div>
  );

  if (phone) {
    const recent = (messages ?? []).filter((m) => m.senderType !== 'system').slice(-2);
    return (
      <Section icon={<MessageSquare />} title={t('Work history')} contentClassName="px-0 pb-0">
        <div className="flex flex-col gap-3 border-t px-3 py-3">
          {recent.map((m) => (
            <Fragment key={m.id}>{message(m)}</Fragment>
          ))}
          {end}
          <Button onClick={() => setOpen(true)} className="w-full" size="lg">
            <MessageSquare /> {t('Open the work history')}
            {(messages?.length ?? 0) > 0 && <span className="text-primary-foreground/70 tabular-nums">{messages!.length}</span>}
          </Button>
        </div>
        {!open && (
          // In thumb reach wherever the page is scrolled to.
          <Button
            onClick={() => setOpen(true)}
            size="icon"
            aria-label={t('Open the work history')}
            className="fixed right-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-30 size-14 rounded-full shadow-lg [&_svg]:size-6"
          >
            <MessageSquare />
          </Button>
        )}
        {open && (
          <ChatScreen title={item.title} subtitle={agentName} onBack={() => setOpen(false)}>
            <div ref={ref} className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
              {thread}
            </div>
            {composer}
          </ChatScreen>
        )}
      </Section>
    );
  }

  // Like a chat with VICKI: the course scrolls, the field to write in stays at the bottom.
  return (
    <section
      aria-label={t('Work history')}
      className="flex h-[min(80vh,900px)] min-h-[28rem] flex-col overflow-hidden rounded-xl border bg-card shadow-xs lg:sticky lg:top-6 lg:h-[max(30rem,calc(100dvh-12rem))]"
    >
      <header className="flex items-center gap-2 border-b px-4 py-3 sm:px-5">
        <MessageSquare className="size-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">{t('Work history')}</h2>
        <span className="min-w-0 truncate text-xs text-muted-foreground">{t('Who did what and what came of it — write here to guide the work.')}</span>
      </header>
      <div ref={ref} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-5 sm:px-6">
        <div className="mx-auto max-w-3xl">{thread}</div>
      </div>
      {composer}
    </section>
  );
}

/** A person writes down what they did themselves, e.g. "Called the customer"; it stands in the course. */
function AddWhatYouDid({ item }: { item: WorkItemDetail }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const add = useApiMutation(() => api.workItems.addHistory(item.id, { summary: text }), {
    invalidate: [qk.workItemHistory(item.id)],
    onSuccess: () => {
      setText('');
      setOpen(false);
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="xs" title={t('Write down what you did yourself, e.g. “Merged the MR by hand”')}>
          <Plus /> {t('I did something')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('What did you do?')}</DialogTitle>
          <DialogDescription>{t('It goes into the work history, so the Lead and its agents know.')}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) add.mutate();
          }}
        >
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t('What did you do? e.g. Called the customer, they confirm the fix')} rows={4} autoFocus />
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={!text.trim()} loading={add.isPending}>
              {t('Add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ChatMessage({
  message: m,
  workspaceId,
  mark,
  agentName,
}: {
  message: Message;
  workspaceId: string;
  /** A person's message the agent at work has not answered yet: whether it read it. */
  mark?: Mark;
  agentName?: string;
}) {
  // A card of VICKI's in one of her tasks (docs/features/front-desk.md#tasks).
  if (m.senderType === 'system' && m.metadata?.event === 'vicki_action_requested' && typeof m.metadata.vickiActionId === 'string') {
    return <VickiActionCard workItemId={m.workItemId} actionId={m.metadata.vickiActionId} />;
  }
  if (m.senderType === 'system' && m.metadata?.event === 'talk_answer') return <TalkAnswer message={m} workspaceId={workspaceId} />;
  if (m.senderType === 'system' && m.metadata?.event !== 'review_decision') {
    // Handed over to another desk from the training: the note links to the Work Item there.
    const handoff = m.metadata?.handoff as { workItemId?: string; deskName?: string } | undefined;
    return (
      <div className="flex justify-center">
        <div className="max-w-[90%] rounded-lg bg-muted px-3 py-1 text-xs break-words text-muted-foreground" title={formatAbsolute(m.createdAt, true)}>
          <Markdown text={m.content} className="gap-1 text-xs" />
          {handoff?.workItemId && (
            <Link
              to="/w/$workspaceId/work-items/$workItemId"
              params={{ workspaceId, workItemId: handoff.workItemId }}
              className="mt-0.5 inline-block font-medium underline underline-offset-2 hover:text-foreground"
            >
              {handoff.deskName ? t('Open it in {desk}', { desk: handoff.deskName }) : t('Open the Work Item')}
            </Link>
          )}
        </div>
      </div>
    );
  }
  if (m.metadata?.event === 'side_chat_request') return <PassedOn message={m} workspaceId={workspaceId} />;
  const mine = m.senderType === 'user';
  const brief = m.metadata?.event === 'run_started';
  return (
    <div className={cn('flex gap-2.5', mine && 'flex-row-reverse')}>
      {!mine && m.senderId ? (
        <AgentAvatar seed={m.senderId} size={28} className="mt-0.5 max-sm:hidden" />
      ) : (
        <div
          className={cn(
            'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full max-sm:hidden',
            mine ? 'bg-primary text-primary-foreground' : 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-300',
          )}
        >
          {mine ? <User className="size-3.5" /> : <Bot className="size-3.5" />}
        </div>
      )}
      {/* On a phone a message takes the width: the avatar moves into the name line, yours stay on the right. */}
      <div className={cn('flex max-w-[80%] min-w-0 flex-col', mine ? 'items-end max-sm:max-w-[88%]' : 'max-sm:max-w-full')}>
        <div className="mb-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {!mine && <Bot className="size-3.5 text-indigo-600 sm:hidden dark:text-indigo-300" />}
          <span className="font-medium text-foreground/80">{m.senderName || (mine ? t('You') : t('Agent'))}</span>
          <ReviewTag message={m} workspaceId={workspaceId} />
          {brief && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-1.5 py-px font-medium text-indigo-700 dark:text-indigo-300">
              <ArrowRight className="size-3" /> {typeof m.metadata?.agentName === 'string' ? t('task for {agent}', { agent: m.metadata.agentName }) : t('started a run')}
            </span>
          )}
          {m.metadata?.event === 'program_report' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-1.5 py-px font-medium text-sky-700 dark:text-sky-300">
              <Info className="size-3" /> {t('report')}
            </span>
          )}
          {m.metadata?.event === 'talk_question' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-1.5 py-px font-medium text-sky-700 dark:text-sky-300">
              <MessageCircleQuestion className="size-3" /> {t('asks {name}', { name: String((m.metadata.to as { name?: string } | undefined)?.name ?? t('an agent')) })}
            </span>
          )}
          {m.metadata?.toLead === true && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-1.5 py-px text-indigo-700 dark:text-indigo-300">
              <Crown className="size-3" /> {t('to the Lead')}
            </span>
          )}
          {m.metadata?.sideChat === true && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-1.5 py-px text-indigo-700 dark:text-indigo-300">
              <Crown className="size-3" /> {t('beside the work')}
            </span>
          )}
          {m.metadata?.event === 'lead_note' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-1.5 py-px text-indigo-700 dark:text-indigo-300">
              <Zap className="size-3" /> {typeof m.metadata?.agentName === 'string' ? t('to {agent}', { agent: m.metadata.agentName }) : t('to the agent at work')}
            </span>
          )}
          {m.metadata?.event === 'side_chat_request' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-1.5 py-px text-indigo-700 dark:text-indigo-300">
              <Zap className="size-3" /> {t('for the next run')}
            </span>
          )}
          {m.metadata?.question === true && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-1.5 py-px text-amber-700 dark:text-amber-300">
              <MessageCircleQuestion className="size-3" /> {m.metadata?.forLead === true ? t('question for the Lead') : t('question')}
            </span>
          )}
          {m.metadata?.event === 'run_finished' && (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-1.5 py-px font-medium',
                m.metadata?.outcome === 'failed' ? 'bg-red-500/10 text-red-700 dark:text-red-300' : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
              )}
            >
              <CircleCheck className="size-3" /> {m.metadata?.outcome === 'failed' ? t('run failed') : t('result for the Lead')}
            </span>
          )}
          <time dateTime={m.createdAt} title={formatAbsolute(m.createdAt, true)}>
            {formatTime(m.createdAt)}
          </time>
          {!mine && <SessionLink executionId={m.executionId} workspaceId={workspaceId} />}
        </div>
        <div
          className={cn(
            'rounded-2xl px-3.5 py-2 text-sm break-words',
            mine ? 'rounded-tr-sm bg-primary whitespace-pre-wrap text-primary-foreground' : 'rounded-tl-sm border bg-card',
            brief && 'border-indigo-200 bg-indigo-50/50 dark:border-indigo-900 dark:bg-indigo-950/30',
            m.metadata?.event === 'run_finished' && m.metadata?.outcome !== 'failed' && 'border-emerald-200 dark:border-emerald-900',
            m.metadata?.event === 'review_decision' && reviewTone(m.metadata.decision),
          )}
        >
          {mine ? m.content : (
            <Expandable long={m.content.length > 1200}>
              <Markdown text={m.content} />
            </Expandable>
          )}
        </div>
        {mark && <ReadMark mark={mark} agentName={agentName ?? t('The agent')} />}
      </div>
    </div>
  );
}

/** Agents reviewing each other: "asked X for a review" / the reviewer's decision, linked to the review sub-item. */
function ReviewTag({ message: m, workspaceId }: { message: Message; workspaceId: string }) {
  const event = m.metadata?.event;
  const reviewId = typeof m.metadata?.reviewWorkItemId === 'string' ? m.metadata.reviewWorkItemId : null;
  if ((event !== 'review_requested' && event !== 'review_decision') || !reviewId) return null;
  const decision = m.metadata?.decision as ReviewDecision | null | undefined;
  const reviewer = m.metadata?.reviewerName == null ? null : String(m.metadata.reviewerName);
  const round = Number(m.metadata?.round) > 1 ? String(m.metadata?.round) : null;
  const label =
    event === 'review_requested'
      ? reviewer
        ? round
          ? t('asked {reviewer} for a review (round {round})', { reviewer, round })
          : t('asked {reviewer} for a review', { reviewer })
        : round
          ? t('asked an agent for a review (round {round})', { round })
          : t('asked an agent for a review')
      : decision
        ? // German keeps the decision's capitals (nouns).
          t('review: {decision}', { decision: inSentence(REVIEW_DECISION[decision]) })
        : t('review: no decision');
  return (
    <Link
      to="/w/$workspaceId/work-items/$workItemId"
      params={{ workspaceId, workItemId: reviewId }}
      className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-px hover:text-primary"
      title={t('Open the review')}
    >
      <ShieldCheck className="size-3" /> {label}
    </Link>
  );
}

function reviewTone(decision: unknown): string {
  if (decision === 'approved') return 'border-emerald-300 dark:border-emerald-800';
  if (decision === 'changes_requested') return 'border-amber-300 dark:border-amber-800';
  return '';
}

/** A long text shows its beginning until opened. */
function Expandable({ long, children }: { long: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  if (!long) return <>{children}</>;
  return (
    <>
      <div className={cn(!open && 'relative max-h-64 overflow-hidden')}>
        {children}
        {!open && <div aria-hidden className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-card to-transparent" />}
      </div>
      <button type="button" onClick={() => setOpen((o) => !o)} className="mt-1 cursor-pointer text-xs font-medium text-primary hover:underline">
        {open ? t('Show less') : t('Show all')}
      </button>
    </>
  );
}

/** The Lead took something a person said beside the work on for its next run: one quiet line, the text when opened. */
function PassedOn({ message: m, workspaceId }: { message: Message; workspaceId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-start gap-2.5 text-xs text-muted-foreground">
      <span className="flex h-5 w-7 shrink-0 items-center justify-center">
        <Crown className="size-3.5 text-amber-600" />
      </span>
      <div className="min-w-0 flex-1 pt-0.5">
        <button type="button" onClick={() => setOpen((o) => !o)} className="cursor-pointer text-left hover:text-foreground">
          {t('{lead} took it on for the next run', { lead: m.senderName ?? t('The Lead') })}
          <span className="ml-1.5 underline-offset-2 hover:underline">{open ? t('Hide') : t('Show')}</span>
        </button>
        <span className="ml-2 inline-flex items-center gap-1.5">
          <time dateTime={m.createdAt} title={formatAbsolute(m.createdAt, true)} className="tabular-nums">
            {formatTime(m.createdAt)}
          </time>
          <SessionLink executionId={m.executionId} workspaceId={workspaceId} />
        </span>
        {open && (
          <div className="mt-1.5 rounded-lg border bg-card px-3 py-2 text-sm text-foreground">
            <Markdown text={m.content} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A sub-item in its parent's thread (an assignment, a hand-off to another desk): when the Lead handed it out, and once it
 * ended, what came back. Its own thread opens in place.
 */
function SubItemEntry({ child, back, parent }: { child: WorkItem; back: boolean; parent: WorkItemDetail }) {
  const [open, setOpen] = useState(false);
  const otherDesk = child.deskId !== parent.deskId;
  const who = otherDesk ? (child.deskName ?? t('another desk')) : (child.assignedAgentName ?? t('the Lead'));
  const link = (
    <Link
      to="/w/$workspaceId/work-items/$workItemId"
      params={{ workspaceId: child.workspaceId, workItemId: child.id }}
      className="font-medium text-foreground hover:text-primary hover:underline"
    >
      {child.title}
    </Link>
  );
  if (back) {
    const failed = child.status !== 'complete';
    return (
      <div className="flex gap-2.5">
        <span
          className={cn(
            'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full max-sm:hidden',
            failed ? 'bg-red-500/15 text-red-600' : 'bg-emerald-500/15 text-emerald-600',
          )}
        >
          {failed ? <CircleX className="size-4" /> : <CircleCheck className="size-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span className="font-medium text-foreground/80">{who}</span>
            <span>{failed ? t('could not finish') : t('finished')}</span>
            {link}
            {child.completedAt && (
              <time dateTime={child.completedAt} title={formatAbsolute(child.completedAt, true)}>
                {formatTime(child.completedAt)}
              </time>
            )}
          </div>
          <div className={cn('rounded-2xl rounded-tl-sm border bg-card px-3.5 py-2 text-sm', failed ? 'border-red-200 dark:border-red-900' : 'border-emerald-200 dark:border-emerald-900')}>
            {child.result?.trim() ? (
              <Expandable long={child.result.length > 900}>
                <Markdown text={child.result} />
              </Expandable>
            ) : (
              <p className="text-muted-foreground">{failed ? t('It ended without a result.') : t('No result was saved.')}</p>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-dashed px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
        <GitBranch className="size-4 shrink-0 text-indigo-500" />
        {otherDesk ? (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            {t('Handed to the desk')} <DeskLink item={child} />:
          </span>
        ) : (
          <span className="text-muted-foreground">{t('Assignment for {agent}', { agent: who })}:</span>
        )}
        {link}
        <WorkItemStatusBadge status={child.status} paused={child.paused} />
        <time className="text-xs text-muted-foreground" dateTime={child.createdAt} title={formatAbsolute(child.createdAt, true)}>
          {formatTime(child.createdAt)}
        </time>
        <button type="button" onClick={() => setOpen((o) => !o)} className="ml-auto cursor-pointer text-xs text-muted-foreground hover:text-foreground hover:underline">
          {open ? t('Hide its thread') : t('Show its thread')}
        </button>
      </div>
      {/* It handed the work on to another desk itself: that hand-off, to follow it there. */}
      {!otherDesk && child.status === 'waiting' && child.waitingReason === 'children' && <OnwardHandoffs child={child} />}
      {open && <ChildThread child={child} />}
    </div>
  );
}

/** The desk a Work Item is on, as a link to that desk (another workspace of the account too). */
function DeskLink({ item }: { item: WorkItem }) {
  return (
    <Link
      to="/w/$workspaceId/desks/$deskId"
      params={{ workspaceId: item.workspaceId, deskId: item.deskId }}
      className="inline-flex items-center gap-1 rounded-md bg-indigo-500/10 px-1.5 py-px font-medium text-indigo-800 hover:bg-indigo-500/15 hover:underline dark:text-indigo-200"
      title={t('Open the desk')}
    >
      {item.deskName ?? t('another desk')}
    </Link>
  );
}

/** What a sub-item of this desk handed on to other desks, each with its desk and its Work Item there. */
function OnwardHandoffs({ child }: { child: WorkItem }) {
  const detail = useQuery({ queryKey: qk.workItemDetail(child.id), queryFn: () => api.workItems.get(child.id), staleTime: 30_000 });
  const onward = (detail.data?.children ?? []).filter((c) => c.deskId !== child.deskId && c.kind !== 'review');
  if (!onward.length) return null;
  return (
    <ul className="mt-1.5 flex flex-col gap-1 pl-6 text-[13px]">
      {onward.map((c) => (
        <li key={c.id} className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <ArrowRight className="size-3.5 shrink-0 text-indigo-500" />
          <span className="text-muted-foreground">{t('Handed to the desk')}</span>
          <DeskLink item={c} />:
          <Link
            to="/w/$workspaceId/work-items/$workItemId"
            params={{ workspaceId: c.workspaceId, workItemId: c.id }}
            className="min-w-0 font-medium text-foreground hover:text-primary hover:underline"
          >
            {c.title}
          </Link>
          <WorkItemStatusBadge status={c.status} paused={c.paused} />
        </li>
      ))}
    </ul>
  );
}

function ChildThread({ child }: { child: WorkItem }) {
  const messages = useQuery({ queryKey: qk.workItemMessages(child.id), queryFn: () => api.workItems.messages(child.id) });
  const shown = (messages.data ?? []).filter((m) => !childrenDone(m));
  return (
    <div className="mt-2 flex max-h-96 flex-col gap-3 overflow-y-auto rounded-lg bg-muted/30 p-3">
      {messages.isPending ? (
        <LoadingRows rows={2} />
      ) : messages.isError ? (
        <ErrorState error={messages.error} onRetry={() => void messages.refetch()} />
      ) : shown.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('No messages yet')}</p>
      ) : (
        shown.map((m) => <ChatMessage key={m.id} message={m} workspaceId={child.workspaceId} />)
      )}
    </div>
  );
}

/**
 * An answer to what the Lead asked from this Work Item (docs/features/desk-talk.md): one of its own agents, or another
 * desk, answering in its own words; the whole exchange stays in the chat it links to.
 */
function TalkAnswer({ message: m, workspaceId }: { message: Message; workspaceId: string }) {
  const meta = m.metadata as { agentId?: string; agentName?: string; deskName?: string; answer?: string; failed?: boolean; talkId?: string; team?: boolean };
  const name = meta.agentName ?? meta.deskName ?? t('Agent');
  const text = typeof meta.answer === 'string' && meta.answer.trim() ? meta.answer : m.content;
  return (
    <div className="flex gap-2.5">
      {meta.agentId ? (
        <AgentAvatar seed={meta.agentId} size={28} className="mt-0.5 max-sm:hidden" />
      ) : (
        <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-sky-600 max-sm:hidden dark:text-sky-300">
          <Bot className="size-3.5" />
        </div>
      )}
      <div className="flex max-w-[80%] min-w-0 flex-col max-sm:max-w-full">
        <div className="mb-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/80">{name}</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-1.5 py-px font-medium text-sky-700 dark:text-sky-300">
            <MessageSquare className="size-3" /> {meta.failed ? t('no answer') : t('answers the Lead')}
          </span>
          <time dateTime={m.createdAt} title={formatAbsolute(m.createdAt, true)}>
            {formatTime(m.createdAt)}
          </time>
          {meta.talkId && (
            <Link to="/w/$workspaceId/conversations/$conversationId" params={{ workspaceId, conversationId: meta.talkId }} className="hover:text-foreground hover:underline">
              {meta.team ? t('Team chat') : t('Chat')}
            </Link>
          )}
        </div>
        <div className={cn('rounded-2xl rounded-tl-sm border bg-card px-3.5 py-2 text-sm break-words', meta.failed ? 'border-amber-200 dark:border-amber-900' : 'border-sky-200 dark:border-sky-900')}>
          <Expandable long={text.length > 1200}>
            <Markdown text={text} />
          </Expandable>
        </div>
      </div>
    </div>
  );
}
